import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { CategoryWithCount, Settings } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { useSettings } from '@/components/generals/settings-provider'
import PageHeader from '@/components/generals/page-header'
import SectionCard from '@/components/generals/section-card'
import LoadError from '@/components/generals/load-error'
import CategoryManager from '@/components/inventory/category-manager'
import BusinessSettingsForm from '@/components/inventory/business-settings-form'
import { describeError } from '@/lib/db'
import { DEFAULT_SETTINGS } from '@/constants/inventory'

import {
    getCategoriesWithCounts,
    getSettings,
    getUncategorizedCount
} from '@/lib/inventory/queries'

type SettingsData = {
    categories: CategoryWithCount[]
    uncategorizedCount: number
}

interface SettingsPageProps {
    settings: Settings
    data: SettingsData | null
}

const SettingsPage: FC<SettingsPageProps> = ({ data }): ReactNode => {
    const { settings } = useSettings()

    return (
        <Layout>
            <Seo
                title='Ajustes'
                description='Configura el nombre de tu negocio, la moneda y las categorías de productos.'
            />

            <div className='flex flex-col gap-6'>
                <PageHeader
                    title='Ajustes'
                    description='Personaliza cómo se muestra tu inventario y organiza los productos por categorías.'
                />

                {data ? (
                    <div className='grid gap-4 lg:grid-cols-5 lg:items-start'>
                        <SectionCard
                            id='negocio'
                            title='Tu negocio'
                            description='Nombre y moneda con la que se calculan los importes.'
                            className='lg:col-span-2'
                        >
                            <BusinessSettingsForm key={`${settings.businessName}-${settings.currency}`} settings={settings} />
                        </SectionCard>

                        <SectionCard
                            id='categorias'
                            title='Categorías'
                            description='Agrupa tus productos por familias. Cada categoría tiene su color.'
                            className='lg:col-span-3'
                            bodyClassName='p-0 sm:p-0'
                        >
                            <CategoryManager categories={data.categories} uncategorizedCount={data.uncategorizedCount} />
                        </SectionCard>
                    </div>
                ) : <LoadError />}
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<SettingsPageProps> = async () => {
    try {
        const [settings, categories, uncategorizedCount] = await Promise.all([
            getSettings(),
            getCategoriesWithCounts(),
            getUncategorizedCount()
        ])

        return { props: { settings, data: { categories, uncategorizedCount } } }
    }
    catch (error) {
        console.error('[ajustes] No se pudieron cargar los ajustes:', describeError(error))

        return { props: { settings: DEFAULT_SETTINGS, data: null } }
    }
}

export default SettingsPage
